import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { ChevronRight, ChevronDown, MapPinOff } from 'lucide-react'
import { apiClient } from '@/services/api'
import { useSessionStore } from '@stores'
import { cn } from '@utils'
import { formatDateString, getShortTime } from '@utils/date'
import { loadGoogleMaps, isGoogleMapsLoaded } from '@utils/googleMaps'
import {
  Countries,
  TrackingStatus,
  TrackingStatusDescriptionStrings,
  TrackingStatusStrings,
  TrackingStatusClass,
  TrackingStatusMissingDeliverDateStrings,
} from '@constants'

interface TrackingDetail {
  scan_date: string
  time_iso: string
  city?: string
  state?: string
  country?: string
  location?: string
  status: number
  message: string
}

interface TrackingData {
  carrier: string
  status: number
  est_delivery_date?: string
  date?: string
  message?: string
  details: TrackingDetail[]
}

interface Fulfillment {
  id: number
  tracking_number: string
  can_track?: boolean
  [key: string]: any
}

interface OrderItem {
  id: number
  show_fulfillments?: boolean
  fulfillments?: Fulfillment[]
  [key: string]: any
}

interface OrderTrackingProps {
  item: OrderItem
  fulfillments?: Fulfillment[]
  onClear?: () => void
  showInputs?: boolean
  onDone?: (input: any) => void
  /** Explicit step to read valid_inputs from; the store session may be a pinned
   *  portal step whose inputs belong to a different screen. */
  session?: any
}

interface TimelineItem {
  date: string | null
  scan_date: string | null
  location: string | null
  status: number
  message: string | null
  expanded: boolean
  events: TimelineEvent[]
}

interface TimelineEvent {
  location?: string
  scan_date: string
  date: string
  message: string
}

interface MarkerInfo {
  location: string
  dates: {
    date: string
    messages: { message: string; time: string }[]
  }[]
  invalid?: boolean
}

declare global {
  interface Window {
    google: any
  }
}

const OrderTrackingSkeleton = () => (
  <div className="w-full">
    <div className="order-tracking">
      <div className="order-tracking-content">
        <div className="order-tracking-left-panel">
          <div className="tracking-info">
            <div className="tracking-carrier-row">
              <div className="h-12 w-12 rounded-md skeleton" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-16 rounded skeleton" />
                <div className="h-5 w-40 rounded skeleton" />
              </div>
            </div>
            <div
              className="rounded-xl p-4 space-y-2"
              style={{ background: 'color-mix(in srgb, var(--theme-color) 4%, var(--content-background-color))' }}>
              <div className="h-3 w-24 rounded skeleton" />
              <div className="h-5 w-2/3 rounded skeleton" />
              <div className="h-3 w-full rounded skeleton" />
              <div className="h-3 w-4/5 rounded skeleton" />
            </div>
            <div className="space-y-3 pt-2">
              <div className="h-4 w-32 rounded skeleton" />
              {[0, 1, 2, 3].map(i => (
                <div key={i} className="flex items-start gap-3">
                  <div className="h-3 w-3 rounded-full skeleton mt-1.5 shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-1/3 rounded skeleton" />
                    <div className="h-3 w-2/3 rounded skeleton" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="order-tracking-right-panel">
          <div className="map-container skeleton" />
        </div>
      </div>
    </div>
  </div>
)

const OrderTracking = ({
  item,
  fulfillments = [],
  onClear,
  showInputs = false,
  onDone,
  session: sessionProp,
}: OrderTrackingProps) => {
  const storeValidInputs = useSessionStore(s => ((s.session as any)?.valid_inputs as any[]) || [])
  const validInputs = ((sessionProp as any)?.valid_inputs as any[]) || storeValidInputs
  const [map, setMap] = useState<any>(null)
  const [tracking, setTracking] = useState<TrackingData | null>(null)
  const [allTrackingInfo, setAllTrackingInfo] = useState<Record<string, TrackingData>>({})
  const [trackingTimeline, setTrackingTimeline] = useState<Map<number, TimelineItem> | null>(null)
  const [selectedFulfillment, setSelectedFulfillment] = useState<Fulfillment | null>(null)
  const [itemFulfillments, setItemFulfillments] = useState<Fulfillment[]>([])
  const [loading, setLoading] = useState(true)
  const [mapsAvailable, setMapsAvailable] = useState(false)
  const [mapsError, setMapsError] = useState(false)
  const [infoWindows, setInfoWindows] = useState<any[]>([])
  const [activeInfoWindow, setActiveInfoWindow] = useState<any>(null)
  const [latestTrackingEvent, setLatestTrackingEvent] = useState<TimelineEvent | null>(null)
  const routeCrossesAntimeridianRef = useRef(false)
  const markerPositionsRef = useRef<any[]>([])
  const mapRef = useRef<any>(null)
  // Guards initMap against re-firing on every parent re-render (initMap's
  // useCallback deps shift constantly, which loops Google's viewport calls).
  const lastInitTrackingRef = useRef<TrackingData | null>(null)

  const mapContainerRef = useRef<HTMLDivElement | null>(null)
  const leftPanelRef = useRef<HTMLDivElement>(null)
  const timelineContainerRef = useRef<HTMLDivElement>(null)
  const trackingInfoHeaderRef = useRef<HTMLDivElement>(null)

  const { session } = useSessionStore()
  const isLegacy = useMemo(() => {
    return session && (session as any).is_legacy && false
  }, [session])

  const styleVariables = (session as any)?.style_variables || {}

  // Computed values
  const hideEstimatedDate = useMemo(() => {
    if (!tracking) {
      return false
    }
    return (
      [
        TrackingStatus.OutForDelivery,
        TrackingStatus.AvailableForPickup,
        TrackingStatus.InTransit,
        TrackingStatus.PreTransit,
      ].indexOf(tracking.status) === -1
    )
  }, [tracking])

  const statusText = useMemo(() => {
    if (!tracking) {
      return ''
    }
    return TrackingStatusStrings[tracking.status] || 'Unknown'
  }, [tracking])

  const statusDescriptionText = useMemo(() => {
    if (!tracking) {
      return ''
    }
    return TrackingStatusDescriptionStrings[tracking.status] || ''
  }, [tracking])

  const isDelivered = useMemo(() => {
    return tracking && tracking.status === TrackingStatus.Delivered
  }, [tracking])

  const isOutForDelivery = useMemo(() => {
    return tracking && tracking.status === TrackingStatus.OutForDelivery
  }, [tracking])

  const estimatedDeliveryDate = useMemo(() => {
    if (!tracking?.est_delivery_date) {
      return null
    }
    return formatDateString(tracking.est_delivery_date)
  }, [tracking])

  const estimatedDateDescriptionText = useMemo(() => {
    if (!tracking) {
      return ''
    }
    return estimatedDeliveryDate
      ? estimatedDeliveryDate
      : TrackingStatusMissingDeliverDateStrings[tracking.status] || ''
  }, [tracking, estimatedDeliveryDate])

  const deliveryAddress = useMemo(() => {
    if (!isDelivered && !isOutForDelivery && session && session.customer) {
      const customer = session.customer as any
      if (customer.ship_state && customer.ship_city) {
        const address = {
          address: customer.ship_address1,
          city: customer.ship_city,
          state: customer.ship_state,
          country: customer.ship_country,
        }
        return {
          ...address,
          location: `${address.address}, ${address.city}, ${address.state} ${address.country}`,
        }
      }
    }
    return null
  }, [isDelivered, isOutForDelivery, session])

  const isNotFoundTracking = useMemo(() => {
    return tracking && tracking.status === TrackingStatus.NotFound
  }, [tracking])

  // Methods
  const isActiveStatus = useCallback(
    (status: number) => {
      return !isNotFoundTracking && latestTrackingEvent && status <= (latestTrackingEvent as any).status
    },
    [isNotFoundTracking, latestTrackingEvent]
  )

  const isCompletedStatus = useCallback(
    (status: number) => {
      return isDelivered || (!isNotFoundTracking && latestTrackingEvent && status < (latestTrackingEvent as any).status)
    },
    [isDelivered, isNotFoundTracking, latestTrackingEvent]
  )

  const getIcon = useCallback(
    (isStart: boolean, isLast: boolean) => {
      const themeColor = (styleVariables as any)?.theme_color || '#71AC00'

      if (isStart) {
        return {
          url:
            'data:image/svg+xml;utf-8,' +
            encodeURIComponent(`
          <svg width="54" height="54" viewBox="0 0 54 54" xmlns="http://www.w3.org/2000/svg">
            <path opacity="0.35" d="M27.0573 53.4934C41.6613 53.4934 53.5003 41.6545 53.5003 27.0504C53.5003 12.4464 41.6613 0.607422 27.0573 0.607422C12.4532 0.607422 0.614258 12.4464 0.614258 27.0504C0.614258 41.6545 12.4532 53.4934 27.0573 53.4934Z" fill="${themeColor}"/>
            <path d="M27.0591 39.7003C34.0444 39.7003 39.7071 34.0376 39.7071 27.0523C39.7071 20.067 34.0444 14.4043 27.0591 14.4043C20.0738 14.4043 14.4111 20.067 14.4111 27.0523C14.4111 34.0376 20.0738 39.7003 27.0591 39.7003Z" fill="#71AC00"/>
            <path d="M27.0591 38.2003C33.216 38.2003 38.2071 33.2092 38.2071 27.0523C38.2071 20.8954 33.216 15.9043 27.0591 15.9043C20.9023 15.9043 15.9111 20.8954 15.9111 27.0523C15.9111 33.2092 20.9023 38.2003 27.0591 38.2003Z" fill="${themeColor}" stroke="white" stroke-width="3"/>
          </svg>
        `),
          anchor: new window.google.maps.Point(27, 27),
        }
      }

      if (isLast) {
        return {
          url:
            'data:image/svg+xml;utf-8,' +
            encodeURIComponent(`
        <svg width="41" height="56" viewBox="0 0 41 56" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20.1 0C14.7712 0.00661555 9.66251 2.12641 5.89446 5.89446C2.12641 9.66251 0.00661555 14.7712 0 20.1C0 33.847 17.985 54.041 18.75 54.894C18.9197 55.083 19.1274 55.2341 19.3593 55.3375C19.5913 55.441 19.8425 55.4944 20.0965 55.4944C20.3505 55.4944 20.6017 55.441 20.8337 55.3375C21.0656 55.2341 21.2733 55.083 21.443 54.894C22.208 54.044 40.193 33.852 40.193 20.1C40.1864 14.7724 38.0676 9.66478 34.301 5.89693C30.5345 2.12908 25.4276 0.00846713 20.1 0ZM20.1 30.208C18.1001 30.208 16.1451 29.6149 14.4823 28.5038C12.8195 27.3926 11.5236 25.8133 10.7584 23.9656C9.99318 22.1179 9.79312 20.0848 10.1835 18.1233C10.5739 16.1619 11.5372 14.3603 12.9515 12.9464C14.3659 11.5325 16.1677 10.5697 18.1293 10.1799C20.0908 9.79012 22.1239 9.99078 23.9714 10.7565C25.8189 11.5223 27.3978 12.8187 28.5084 14.4818C29.6191 16.145 30.2116 18.1001 30.211 20.1C30.2102 22.7811 29.1446 25.3521 27.2485 27.2476C25.3524 29.1431 22.7811 30.208 20.1 30.208Z" fill="${themeColor}"/>
        </svg>
      `),
        }
      }

      return {
        url:
          'data:image/svg+xml;utf-8,' +
          encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" fill="none">
          <path d="M12.648 23.796C18.8049 23.796 23.796 18.8049 23.796 12.648C23.796 6.49113 18.8049 1.5 12.648 1.5C6.49113 1.5 1.5 6.49113 1.5 12.648C1.5 18.8049 6.49113 23.796 12.648 23.796Z" fill="${themeColor}" stroke="white" stroke-width="3"/>
        </svg>
      `),
        anchor: new window.google.maps.Point(13, 13),
      }
    },
    [styleVariables]
  )

  const initializeTimeline = useCallback(() => {
    const defaultTracking: TimelineItem = {
      date: null,
      scan_date: null,
      location: null,
      status: 0,
      message: null,
      expanded: false,
      events: [],
    }

    setTrackingTimeline(
      new Map([
        [
          TrackingStatus.Delivered,
          {
            ...defaultTracking,
            status: TrackingStatus.Delivered,
            location: deliveryAddress?.location || null,
          },
        ],
        [TrackingStatus.OutForDelivery, { ...defaultTracking, status: TrackingStatus.OutForDelivery }],
        [TrackingStatus.InTransit, { ...defaultTracking, status: TrackingStatus.InTransit }],
        [TrackingStatus.PreTransit, { ...defaultTracking, status: TrackingStatus.PreTransit }],
      ])
    )
  }, [deliveryAddress])

  const closeAllInfoWindows = useCallback(() => {
    infoWindows.forEach(infoWindow => {
      infoWindow.close()
    })
    setActiveInfoWindow(null)
  }, [infoWindows])

  // Latest-callback ref so map listeners (bound once) call the current version
  // instead of a stale closure over an earlier `infoWindows` array.
  const closeAllInfoWindowsRef = useRef<() => void>(() => {})
  closeAllInfoWindowsRef.current = closeAllInfoWindows

  const toggleScrollbar = useCallback(() => {
    if (onClear) {
      onClear()
    }
  }, [onClear])

  const getStatusText = useCallback((status: number) => {
    return TrackingStatusStrings[status] || 'Unknown'
  }, [])

  const toggleDetail = useCallback(
    (status: number) => {
      if (!trackingTimeline) {
        return
      }

      const selectedDetail = trackingTimeline.get(status)
      if (selectedDetail && selectedDetail.events && selectedDetail.events.length > 0) {
        const newTimeline = new Map(trackingTimeline)

        for (const [_, detail] of newTimeline.entries()) {
          if (detail.status === status) {
            detail.expanded = !detail.expanded
          } else {
            detail.expanded = false
          }
        }

        setTrackingTimeline(newTimeline)

        // Scroll to element
        setTimeout(() => {
          const element = document.querySelector(`[data-timeline-status="${status}"]`)
          if (element && timelineContainerRef.current && trackingInfoHeaderRef.current) {
            const isSmallScreen = window.innerWidth < 768
            const container = isSmallScreen ? leftPanelRef.current : timelineContainerRef.current
            if (!container) {
              return
            }

            const elementTop = (element as HTMLElement).offsetTop
            const scrollOffset = elementTop + (isSmallScreen ? trackingInfoHeaderRef.current.offsetHeight - 40 : 10)

            const allCollapsed = Array.from(newTimeline.values()).every(timeline => !timeline.expanded)

            container.scrollTo({
              top: allCollapsed ? 0 : scrollOffset,
              behavior: 'smooth',
            })
          }
        }, 100)
      }
    },
    [trackingTimeline]
  )

  const extractUniqueLocationsWithMessages = useCallback((events: TrackingDetail[]): MarkerInfo[] => {
    const result: MarkerInfo[] = []

    if (events && events.length > 0) {
      const countries = (Object.values(Countries) as string[])
        .concat(Object.keys(Countries) as string[])
        .concat(['usa', 'united states', 'hk', 'cn', 'china', 'hong kong'] as string[])
        .map(country => country.toLowerCase())

      let currentLocation: string | null = null
      let locationGroup: MarkerInfo | null = null

      const sortedEvents = [...events].sort((a, b) => {
        return new Date(a.time_iso).getTime() - new Date(b.time_iso).getTime()
      })

      sortedEvents.forEach(event => {
        const eventLocation = event.location || ''
        const eventDate = formatDateString(event.scan_date, false, true)
        const onlyCountry = countries.indexOf(eventLocation.toLowerCase()) > -1

        if (!onlyCountry || event.status === TrackingStatus.Delivered) {
          if (eventLocation !== currentLocation) {
            if (currentLocation !== null && locationGroup !== null) {
              result.push(locationGroup)
            }

            currentLocation = eventLocation
            locationGroup = {
              location: eventLocation,
              dates: [],
            }
          }
        }

        if (locationGroup) {
          let dateGroup = locationGroup.dates.find(dg => dg.date === eventDate)

          if (!dateGroup) {
            dateGroup = {
              date: eventDate,
              messages: [],
            }
            locationGroup.dates.push(dateGroup)
          }

          dateGroup.messages.push({
            message: event.message,
            time: getShortTime(event.scan_date),
          })
        }
      })

      if (currentLocation !== null && locationGroup !== null) {
        result.push(locationGroup)
      }
    }

    return result
  }, [])

  const getUniqueLocations = useCallback(() => {
    if (!tracking || !trackingTimeline) {
      return []
    }
    return extractUniqueLocationsWithMessages(tracking.details)
  }, [tracking, trackingTimeline, extractUniqueLocationsWithMessages])

  const geocodeAddress = useCallback(
    (
      address: string
    ): Promise<{
      address: string
      coordinates: { lat: number; lng: number }
    }> => {
      return new Promise((resolve, reject) => {
        const geocoder = new window.google.maps.Geocoder()
        geocoder.geocode({ address }, (results: any, status: string) => {
          if (status === 'OK' && results[0]) {
            resolve({
              address: address.length <= results[0].formatted_address ? results[0].formatted_address : address,
              coordinates: {
                lat: results[0].geometry.location.lat(),
                lng: results[0].geometry.location.lng(),
              },
            })
          } else {
            reject(status)
          }
        })
      })
    },
    []
  )

  const initializeMap = useCallback((centerPosition: any, positions: any[]) => {
    if (!mapContainerRef.current) {
      return
    }

    const hasPositions = positions && positions.length > 0
    const newMap = new window.google.maps.Map(mapContainerRef.current, {
      center: centerPosition,
      zoom: positions.length > 1 ? 12 : 5,
      disableDefaultUI: hasPositions,
      fullscreenControl: hasPositions,
      zoomControl: hasPositions,
      mapTypeControl: hasPositions,
      gestureHandling: hasPositions ? 'auto' : 'none',
      keyboardShortcuts: hasPositions,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }],
        },
      ],
    })

    // Tapping the map background closes any open info window — gives users
    // a second escape hatch beyond Google's own close X.
    newMap.addListener('click', () => {
      closeAllInfoWindowsRef.current?.()
    })

    setMap(newMap)
    mapRef.current = newMap
    return newMap
  }, [])

  const addClickHandler = useCallback(
    (marker: any, infoWindow: any) => {
      marker.addListener('click', () => {
        if (activeInfoWindow === infoWindow) {
          closeAllInfoWindows()
        } else {
          closeAllInfoWindows()
          infoWindow.open(map, marker)
          setActiveInfoWindow(infoWindow)
        }
      })
    },
    [map, activeInfoWindow, closeAllInfoWindows]
  )

  const addInfoWindowToMarker = useCallback(
    (position: any, mapMarker: any, markerInfo: MarkerInfo) => {
      let messagesHtml = ''

      if (markerInfo.dates) {
        messagesHtml = '<ul class="marker-messages">'
        for (const detail of markerInfo.dates) {
          messagesHtml += `
          <li class="marker-message-item">
            <p class="marker-message-date">${detail.date}</p>
        `

          detail.messages.forEach(item => {
            messagesHtml += `<p class="marker-message-description">
            <b>${item.time}</b> -
            ${item.message}
          </p>`
          })

          messagesHtml += '</li>'
        }

        messagesHtml += '</ul>'
      }

      const infoWindow = new window.google.maps.InfoWindow({
        // Render our own header + close button so we control the layout
        // regardless of Google's default InfoWindow chrome version drift.
        headerDisabled: true,
        content: `
          <div class="marker-address">
            <button type="button" class="marker-close" aria-label="Close">&times;</button>
            <p class="marker-address-heading">${markerInfo.location}</p>
            ${messagesHtml}
          </div>
      `,
        position: position,
      })

      // After the InfoWindow attaches to the DOM, wire its close button.
      infoWindow.addListener('domready', () => {
        const closeBtn = document.querySelector('.marker-address .marker-close') as HTMLElement | null
        if (closeBtn && !(closeBtn as any)._wired) {
          ;(closeBtn as any)._wired = true
          closeBtn.addEventListener('click', () => infoWindow.close())
        }
      })

      addClickHandler(mapMarker, infoWindow)
      setInfoWindows(prev => [...prev, infoWindow])
    },
    [addClickHandler]
  )

  const addMarkerToMap = useCallback((currentMap: any, position: any, icon: any) => {
    return new window.google.maps.Marker({
      position: position,
      map: currentMap,
      icon: icon,
    })
  }, [])

  const checkAntimeridianRoute = useCallback((coordinates: any[]) => {
    routeCrossesAntimeridianRef.current = false
    for (let i = 1; i < coordinates.length; i++) {
      const lonDiff = coordinates[i].lng - coordinates[i - 1].lng
      if (Math.abs(lonDiff) > 180) {
        routeCrossesAntimeridianRef.current = true
        return true
      }
    }
    return false
  }, [])

  const calculateCenterForAntimeridianRoute = useCallback((positions: any[]) => {
    if (routeCrossesAntimeridianRef.current) {
      let sumLat = 0
      let sumLng = 0
      for (const point of positions) {
        sumLat += point.lat
        sumLng += point.lng
      }
      return { lat: sumLat / positions.length, lng: sumLng / positions.length }
    } else if (positions.length) {
      return positions[0]
    }
    return { lat: 40.54241, lng: -103.5004173 }
  }, [])

  const normalizePolylinePath = useCallback((points: any[]) => {
    if (points && points.length > 1) {
      const normalizedPoints = [points[0]]
      for (let i = 1; i < points.length; i++) {
        const prevPoint = normalizedPoints[i - 1]
        const currentPoint = points[i]

        let adjustedLng = currentPoint.lng
        const lngDifference = adjustedLng - prevPoint.lng

        if (lngDifference > 180) {
          adjustedLng -= 360
        } else if (lngDifference < -180) {
          adjustedLng += 360
        }

        normalizedPoints.push({
          lat: currentPoint.lat,
          lng: adjustedLng,
        })
      }
      return normalizedPoints
    }
    return points
  }, [])

  const addPathToMarkers = useCallback(
    (currentMap: any, positions: any[]) => {
      const normalizedPositions = normalizePolylinePath(positions)
      const themeColor = (styleVariables as any)?.theme_color || '#71AC00'

      for (let index = 0; index < normalizedPositions.length; index++) {
        if (index >= 0) {
          const positionPath =
            index === 0 ? [normalizedPositions[index]] : [normalizedPositions[index - 1], normalizedPositions[index]]

          const path = new window.google.maps.Polyline({
            path: positionPath,
            geodesic: !routeCrossesAntimeridianRef.current,
            strokeOpacity: 0,
            strokeWeight: 3,
            icons: [
              {
                icon: {
                  path: 'M 0,-1 0,1',
                  strokeOpacity: 1,
                  scale: 4,
                  strokeColor: themeColor,
                },
                offset: '0',
                repeat: '18px',
              },
            ],
          })
          path.setMap(currentMap)
        }
      }
    },
    [normalizePolylinePath, styleVariables]
  )

  const initMap = useCallback(async () => {
    if (!isGoogleMapsLoaded() || !mapContainerRef.current) {
      return
    }

    const positions: any[] = []
    const locations: Record<string, any> = {}
    let markers = getUniqueLocations()

    for (const marker of markers) {
      if (locations[marker.location]) {
        const position = { ...locations[marker.location] }
        positions.push(position)
      } else {
        try {
          const { address, coordinates } = await geocodeAddress(marker.location)
          marker.location = address
          positions.push(coordinates)
          locations[marker.location] = coordinates
        } catch (err) {
          marker.invalid = true
        }
      }
    }

    markers = markers.filter(marker => !marker.invalid)
    checkAntimeridianRoute(positions)

    const center = calculateCenterForAntimeridianRoute(positions)
    const currentMap = initializeMap(positions.length > 0 ? center : { lat: 40.54241, lng: -103.5004173 }, positions)

    if (!currentMap) {
      return
    }

    setInfoWindows([])

    positions.forEach((pos, index) => {
      const icon = getIcon(index === positions.length - 1, index === 0)
      const mapMarker = addMarkerToMap(currentMap, pos, icon)
      addInfoWindowToMarker(pos, mapMarker, markers[index])
    })

    addPathToMarkers(currentMap, positions)

    markerPositionsRef.current = positions
    const bounds = new window.google.maps.LatLngBounds()
    positions.forEach(p => bounds.extend(p))
    if (positions.length > 1) {
      currentMap.fitBounds(bounds)
    }
  }, [
    getUniqueLocations,
    geocodeAddress,
    checkAntimeridianRoute,
    calculateCenterForAntimeridianRoute,
    initializeMap,
    getIcon,
    addMarkerToMap,
    addInfoWindowToMarker,
    addPathToMarkers,
  ])

  const getStatusClass = useCallback((status: number) => {
    const cls = TrackingStatusClass[status]
    return cls ? `is-${cls}` : ''
  }, [])

  const getTrackingData = useCallback(async () => {
    if (!item) {
      return
    }

    if (!fulfillments || !fulfillments.length) {
      setLoading(true)
      try {
        const response = await apiClient.get(`/order_items/${item.id}/fulfillments/`)
        const trackableFulfillments = response.data.filter((item: Fulfillment) => item.can_track)
        if (trackableFulfillments && trackableFulfillments.length) {
          setItemFulfillments(trackableFulfillments)
          updateSelectedFulfillment(trackableFulfillments[0])
        } else {
          setLoading(false)
        }
      } catch (error) {
        console.error(error)
        setLoading(false)
      }
    } else {
      setItemFulfillments(fulfillments)
      updateSelectedFulfillment(fulfillments[0])
    }
  }, [item, fulfillments])

  const prepareTrackingTimeline = useCallback(() => {
    if (!tracking || !trackingTimeline) {
      return
    }

    const newTimeline = new Map(trackingTimeline)
    let newLatestEvent: any = null

    for (const [index, detail] of tracking.details.entries()) {
      let location = ''

      if (detail.city && detail.state) {
        location = `${detail.city}, ${detail.state}, ${detail.country}`
      }

      location = location || detail.location || ''
      let message = detail.message || ''

      let status = detail.status

      if ([TrackingStatus.Unknown].indexOf(detail.status) > -1) {
        status = TrackingStatus.InTransit
      }

      if ([TrackingStatus.ReturnToSender, TrackingStatus.Failure].indexOf(detail.status) > -1) {
        status = TrackingStatus.OutForDelivery
      }

      if (detail.status === TrackingStatus.AvailableForPickup) {
        if (tracking.details.length > index + 1) {
          status = tracking.details[index + 1].status
        }
      }

      if (status === TrackingStatus.InTransit) {
        message = message + (message.endsWith('in') ? ' ' : ', ') + location
      } else {
        message = location + (location ? ' - ' : '') + message
      }

      const trackingInfo = newTimeline.get(status)
      let updateStatus: TimelineItem = {
        ...trackingInfo,
        events: trackingInfo?.events || [],
      } as TimelineItem

      if (!trackingInfo?.scan_date) {
        updateStatus = {
          date: formatDateString(detail.scan_date),
          scan_date: detail.scan_date,
          location: location,
          status: status,
          message: message,
          expanded: false,
          events: [],
        }

        if (status !== TrackingStatus.Delivered && message) {
          updateStatus.events.push({
            location: location,
            scan_date: detail.scan_date,
            date: formatDateString(detail.scan_date),
            message: message,
          })
        }
      } else {
        updateStatus.events.push({
          ...(location
            ? {
                location: location,
                date: formatDateString(detail.scan_date),
              }
            : {}),
          scan_date: detail.scan_date,
          message: message,
        } as TimelineEvent)
      }

      newTimeline.set(status, updateStatus as TimelineItem)
    }

    if (newTimeline) {
      for (const [_, detail] of newTimeline.entries()) {
        if (detail && detail.events.length) {
          detail.events = detail.events.sort(
            (a, b) => new Date(b.scan_date).getTime() - new Date(a.scan_date).getTime()
          )
        }
        if (detail.scan_date && !newLatestEvent) {
          newLatestEvent = { ...detail }
          detail.expanded = true
          newTimeline.set(detail.status, detail)
        }
      }
    }

    if (tracking.status === TrackingStatus.NotFound) {
      newLatestEvent = {
        date: formatDateString(tracking.date || ''),
        scan_date: tracking.date,
        status: TrackingStatus.NotFound,
        message: tracking.message,
      }
    }

    setTrackingTimeline(newTimeline)
    setLatestTrackingEvent(newLatestEvent)
  }, [tracking, trackingTimeline])

  const preparedMarkerIdsRef = useRef<Set<string>>(new Set())

  const prepareMarkerLocations = useCallback(() => {
    if (!tracking) {
      return
    }

    const key = selectedFulfillment ? selectedFulfillment.id.toString() : 'current'
    if (preparedMarkerIdsRef.current.has(key)) {
      return
    }

    tracking.details.forEach(detail => {
      let location = ''
      const country = detail.country || 'US'
      if (detail.city && detail.state) {
        location = `${detail.city}, ${detail.state} ${country}`
      }
      detail.location = location || detail.location || ''
    })
    preparedMarkerIdsRef.current.add(key)
  }, [tracking, selectedFulfillment])

  const postFetchTrackingData = useCallback(() => {
    try {
      initializeTimeline()
      prepareMarkerLocations()
      prepareTrackingTimeline()
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [initializeTimeline, prepareMarkerLocations, prepareTrackingTimeline])

  const updateSelectedFulfillment = useCallback(
    async (fulfillment: Fulfillment) => {
      if (selectedFulfillment && selectedFulfillment.id === fulfillment.id) {
        return
      }
      setSelectedFulfillment(fulfillment)

      const cached = allTrackingInfo[fulfillment.id.toString()]
      if (cached) {
        setTracking(cached)
        postFetchTrackingData()
        setLoading(false)
        return
      }

      setLoading(true)
      try {
        const result = await apiClient.get(`/fulfillments/${fulfillment.id}/`)
        // Empty/missing tracking is treated as Not Found (not null) so the left
        // panel still shows the status + timeline and the map renders behind.
        const trackingData: TrackingData = result.data.tracking || {
          carrier: '',
          status: TrackingStatus.NotFound,
          details: [],
          date: new Date().toISOString(),
          message: 'Tracking not found',
        }
        setTracking(trackingData)
        setAllTrackingInfo(prev => ({
          ...prev,
          [fulfillment.id.toString()]: trackingData,
        }))
        postFetchTrackingData()
      } catch {
        const notFoundTracking: TrackingData = {
          carrier: '',
          status: TrackingStatus.NotFound,
          details: [],
          date: new Date().toISOString(),
          message: 'Tracking not found',
        }
        setTracking(notFoundTracking)
        setAllTrackingInfo(prev => ({
          ...prev,
          [fulfillment.id.toString()]: notFoundTracking,
        }))
        postFetchTrackingData()
        setLoading(false)
      }
    },
    [selectedFulfillment, allTrackingInfo, postFetchTrackingData]
  )

  // Key off `item?.id` only — `getTrackingData` reference shifts on every
  // session-poll tick which would re-fire the fetch.
  useEffect(() => {
    if (!item?.id) {
      return
    }
    getTrackingData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id])

  useEffect(() => {
    let cancelled = false
    const initMaps = async () => {
      try {
        await loadGoogleMaps()
        if (!cancelled) {
          setMapsAvailable(true)
          setMapsError(false)
        }
      } catch (err) {
        if (!cancelled) {
          setMapsAvailable(false)
          setMapsError(true)
          console.warn('Google Maps unavailable:', err)
        }
      }
    }
    initMaps()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!mapsAvailable || !tracking || loading) {
      return
    }
    if (lastInitTrackingRef.current === tracking) {
      return
    }
    lastInitTrackingRef.current = tracking
    initMap()
  }, [mapsAvailable, tracking, loading, initMap])

  useEffect(() => {
    if (!tracking) {
      lastInitTrackingRef.current = null
    }
  }, [tracking])

  // prepareTrackingTimeline() runs synchronously right after setTracking(), so it
  // reads stale `tracking` and bails before its NotFound branch fires. Set the
  // synthetic "Not Found" event here once `tracking` has committed so the status
  // header (badge + carrier message) renders.
  useEffect(() => {
    if (tracking && tracking.status === TrackingStatus.NotFound) {
      setLatestTrackingEvent({
        date: formatDateString(tracking.date || ''),
        scan_date: tracking.date,
        status: TrackingStatus.NotFound,
        message: tracking.message,
      } as TimelineEvent)
    }
  }, [tracking])

  const setMapContainerRef = useCallback((node: HTMLDivElement | null) => {
    mapContainerRef.current = node
  }, [])

  const recenterMap = useCallback(() => {
    const currentMap = mapRef.current
    const positions = markerPositionsRef.current
    if (!currentMap || !positions || positions.length === 0 || !window.google) {
      return
    }
    const bounds = new window.google.maps.LatLngBounds()
    positions.forEach(p => bounds.extend(p))
    if (positions.length > 1) {
      currentMap.fitBounds(bounds)
    } else {
      currentMap.setCenter(positions[0])
    }
  }, [])

  useEffect(() => {
    window.addEventListener('resize', recenterMap)
    return () => window.removeEventListener('resize', recenterMap)
  }, [recenterMap])

  if (loading) {
    return <OrderTrackingSkeleton />
  }

  return (
    <div className="w-full">
      <div className="order-tracking">
        {/* Main Content */}
        {item && itemFulfillments.length > 0 && (
          <div className="order-tracking-content">
            {/* Left Panel - Tracking Details */}
            <div ref={leftPanelRef} className="order-tracking-left-panel">
              <div className="tracking-info">
                <div className="tracking-carrier-row">
                  {tracking?.carrier && (
                    <div className="tracking-carrier-logo">
                      <img
                        src={`/assets/images/tracking-logos/${(tracking.carrier || '').replaceAll(' ', '')}.png`}
                        alt={tracking.carrier}
                        onError={e => {
                          ;(e.target as HTMLImageElement).style.display = 'none'
                        }}
                      />
                    </div>
                  )}
                  <div className="tracking-number-block">
                    <div className="tracking-number-label">Tracking#</div>
                    {itemFulfillments.map(fulfillment => (
                      <div
                        key={`track-${fulfillment.tracking_number}`}
                        className={cn('tracking-number-value', itemFulfillments.length <= 1 && 'is-static')}
                        onClick={() => updateSelectedFulfillment(fulfillment)}>
                        <span>{fulfillment.tracking_number}</span>
                        {itemFulfillments.length > 1 && selectedFulfillment?.id === fulfillment.id && (
                          <ChevronRight className="hidden lg:block" size={14} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {tracking && (
                  <>
                    <div ref={trackingInfoHeaderRef} className="tracking-status-row">
                      {!hideEstimatedDate && (
                        <div className="tracking-status-card">
                          <span className="tracking-status-label">Estimated Date</span>
                          {!estimatedDeliveryDate && (
                            <span className="tracking-status-heading">Estimating Delivery...</span>
                          )}
                          <span
                            className={cn('tracking-status-description', estimatedDeliveryDate && 'is-estimated-date')}>
                            {estimatedDateDescriptionText}
                          </span>
                        </div>
                      )}

                      {latestTrackingEvent && (
                        <div className="tracking-status-card">
                          <span
                            className={cn(
                              'tracking-status-heading tracking-status-badge',
                              getStatusClass(tracking.status)
                            )}>
                            {statusText}
                          </span>
                          <span className="tracking-status-description">
                            {isDelivered && latestTrackingEvent
                              ? (latestTrackingEvent as any).date
                              : statusDescriptionText}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-3">
                      <div className="tracking-timeline-header">Shipping Details</div>
                      <div ref={timelineContainerRef} className="tracking-timeline">
                        {trackingTimeline &&
                          Array.from(trackingTimeline.entries()).map(([status, item]) => (
                            <div
                              key={`status-${status}`}
                              data-timeline-status={status}
                              className="tracking-timeline-item">
                              <div className="tracking-timeline-marker">
                                <div className={cn('tracking-timeline-line', isActiveStatus(status) && 'is-active')} />
                                <div
                                  className={cn(
                                    'tracking-timeline-dot',
                                    isActiveStatus(status) && 'is-active',
                                    isCompletedStatus(status) && 'is-completed'
                                  )}
                                />
                              </div>

                              <div className="tracking-timeline-content">
                                <div className="tracking-timeline-heading" onClick={() => toggleDetail(status)}>
                                  <h3>{getStatusText(status)}</h3>
                                  {item.events && item.events.length > 0 && (
                                    <ChevronDown
                                      className={cn('tracking-timeline-chevron', item.expanded && 'is-open')}
                                      size={14}
                                      color="#4E6574"
                                      strokeWidth={2}
                                    />
                                  )}
                                </div>
                                <p className="tracking-timeline-location">
                                  {item.location && <span>{item.location}</span>}
                                  {item.location && item.date && <span> - </span>}
                                  {item.date && <span>{item.date}</span>}
                                </p>
                                {item.events && item.events.length > 0 && item.expanded && (
                                  <ul className="tracking-events list-none m-0 p-0">
                                    {item.events.map((event, i) => (
                                      <li key={i} className="tracking-event">
                                        <div className="tracking-event-time">{event.date}</div>
                                        <div className="tracking-event-description">{event.message}</div>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  </>
                )}

                {session && (session as any).can_undo && (
                  <div className="flex justify-center pt-2">
                    <button className="btn-base input-choice-btn input-btn" onClick={toggleScrollbar}>
                      Return to Order
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="order-tracking-right-panel">
              <div className="map-container">
                {mapsAvailable && tracking ? (
                  <>
                    <div id="map" ref={setMapContainerRef}></div>
                    {isNotFoundTracking && (
                      <div className="map-error-overlay">
                        <div className="map-error-card">
                          <MapPinOff size={40} strokeWidth={1.6} />
                          <p className="map-error-title">No tracking data</p>
                          <p className="map-error-description">
                            We couldn't find tracking information for this shipment. Please check back later or contact
                            support if the issue persists.
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  mapsError && (
                    <div className="map-error-state">
                      <MapPinOff size={40} strokeWidth={1.6} />
                      <p className="map-error-title">Map couldn't load</p>
                      <p className="map-error-description">
                        We couldn't display the delivery map right now. Your tracking details above are still up to
                        date.
                      </p>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        )}

        {showInputs && validInputs.length > 0 && (
          <div className="tracking-info-actions flex flex-wrap justify-center gap-3 w-full py-4">
            {validInputs.map((input: any) => (
              <button
                key={input.id ?? input.input_id}
                type="button"
                className="btn-base input-choice-btn"
                onClick={() => onDone?.(input)}
                dangerouslySetInnerHTML={{ __html: input.formatted_label || '' }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default OrderTracking
